// How the web looks for everyone (Administration -> Appearance) - text size,
// Kannada labels, date style, rows per page, menu on start, header name.
// Saved on the server (GET/PUT /system-settings/appearance) and cached in
// this browser so it applies the moment the page opens - including labels
// some pages work out once at load time. When the server's copy differs from
// the cache, the page reloads once so everything picks it up.
import apiClient from '../services/api';

export interface Appearance {
  font_scale: number; // % of normal text size
  show_kannada: boolean;
  date_format: 'dd MMM yyyy' | 'dd/MM/yyyy' | 'yyyy-MM-dd';
  page_size: 10 | 20 | 50 | 100;
  table_density: 'compact' | 'comfortable'; // row height in every table
  sidebar_default: 'shown' | 'hidden';
  header_title: string;
  header_subtitle: string;
}

export const APPEARANCE_DEFAULTS: Appearance = {
  font_scale: 100, show_kannada: true, date_format: 'dd MMM yyyy', page_size: 20, table_density: 'compact',
  sidebar_default: 'shown', header_title: 'Sri KalpaGavya', header_subtitle: 'ILMS',
};

const CACHE_KEY = 'ilms.appearance';

function readCache(): Appearance {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? { ...APPEARANCE_DEFAULTS, ...JSON.parse(raw) } : APPEARANCE_DEFAULTS;
  } catch {
    return APPEARANCE_DEFAULTS;
  }
}

let current = readCache();

export function getAppearance(): Appearance {
  return current;
}

/** Text size for the whole app - everything is sized in rem. */
export function applyFontScale(scale = current.font_scale): void {
  document.documentElement.style.fontSize = `${scale}%`;
}

function remember(a: Appearance): boolean {
  const changed = JSON.stringify(a) !== JSON.stringify(current);
  current = a;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(a));
  } catch {
    // Storage blocked - still applies for this visit.
  }
  return changed;
}

/** After sign-in: fetch the farm's appearance; reload once if it changed. */
export async function syncAppearance(): Promise<void> {
  try {
    const { data } = await apiClient.get<Appearance>('/system-settings/appearance');
    if (remember({ ...APPEARANCE_DEFAULTS, ...data })) window.location.reload();
  } catch {
    // Offline or not signed in - keep the cached look.
  }
}

export async function saveAppearance(a: Appearance): Promise<Appearance> {
  const { data } = await apiClient.put<Appearance>('/system-settings/appearance', a);
  remember({ ...APPEARANCE_DEFAULTS, ...data });
  return data;
}
